import { CommonServiceIds } from 'azure-devops-extension-api'
import { isNull } from 'lodash'

import { ROUTE_PATHS } from '@/routes/route-helpers'
import { getAppType } from '@/util'
import {
  dispatchUrlByAzureNavService,
  getAzureExtensionLocation
} from '@/utils/azure-devops-helpers/azure-history'
import { getAzureService } from '@/utils/azure-devops-helpers/azure-service'
import { AZURE_DEVOPS_SDK } from '@/utils/azure-devops-helpers/general'
import { getAzureProjectContext } from '@/utils/azure-devops-helpers/project-context'
import { getWorkItemNavService } from '@/utils/azure-devops-helpers/work-item'
import { getRefererModuleName } from '@/utils/external-link-referer'
import { getForgeContext, getForgeExtensionLocation } from '@/utils/forge-helpers/forge-context'
import { FORGE_ENVIRONMENT_ID, FORGE_APP_ID, forgeRouter } from '@/utils/forge-helpers/general'
import { isFirefox, isSafari } from '@/utils/general'
import { NOTIFICATION_TYPES, showNotify } from '@/utils/notify'
import { isKR, isObjective, isWorkItem, objectiveIsJiraTask } from '@/utils/objective-types'
import { FILTER_DEFAULT_VALUES, FILTERS_KEYS } from '@/utils/okr-elements/filters'
import {
  EDIT_ELEMENT_QUERY_KEYS,
  EDIT_WORKSPACE_QUERY_KEY,
  OPENED_NOTIFICATIONS_KEY,
  REFERER
} from '@/utils/query-parameters'
import { IS_DEVELOPMENT } from '@root/app-modes'
import { APP_PLATFORMS } from '@root/app-platforms'

export const CONFLUENCE_MACRO_MODULE_TYPE = 'macro'
export const JIRA_MAIN_APP_MODULE_TYPE = 'jira:globalPage'
export const JIRA_GADGET_MODULE_TYPE = 'jira:dashboardGadget'

const getOkrElementsListQuery = ({ predefinedParams = {}, withEmptyFilters = false }) => {
  const defaultQuery = Object.entries(predefinedParams).reduce((acc, [key, value]) => {
    const query = `${key}=${value}`
    return acc ? `${acc}&${query}` : `${query}`
  }, '')

  if (withEmptyFilters) {
    const excludedParams = Object.keys(predefinedParams)

    const emptyFiltersQuery = Object.entries(FILTER_DEFAULT_VALUES)
      .filter(([key]) => {
        return !excludedParams.includes(key)
      })
      .reduce((acc, [key, value]) => {
        const valueForQuery = Array.isArray(value) ? JSON.stringify(value) : value
        const query = `${key}=${valueForQuery}`
        return acc ? `${acc}&${query}` : `${query}`
      }, defaultQuery)

    return emptyFiltersQuery
  }

  return defaultQuery
}

//     intervalId && `${FILTERS_KEYS.INTERVAL_IDS}=[${intervalId}]`,
export const createQueryForExternalLink = ({
  id,
  typeId,
  intervalId,
  levelId,
  source,
  withEmptyFilters = false,
  isJiraApp = true
}) => {
  const [queryParameter] = [
    isObjective({ typeId }) && `${EDIT_ELEMENT_QUERY_KEYS.EDIT_OBJECTIVE}=${id}`,
    isKR({ typeId }) && `${EDIT_ELEMENT_QUERY_KEYS.EDIT_KR}=${id}`,
    objectiveIsJiraTask({ typeId }) && `${EDIT_ELEMENT_QUERY_KEYS.EDIT_LINK_JIRA_ISSUE}=${id}`,
    isWorkItem({ typeId }) && `${EDIT_ELEMENT_QUERY_KEYS.EDIT_LINK_WORK_ITEM}=${id}`,
    intervalId &&
      getOkrElementsListQuery({
        predefinedParams: {
          [FILTERS_KEYS.INTERVAL_IDS]: JSON.stringify([intervalId])
        },
        withEmptyFilters
      }),
    levelId && `${EDIT_ELEMENT_QUERY_KEYS.EDIT_OBJECTIVE}=${id}&levelId=${levelId}`
  ].filter(Boolean)
  const referer = getRefererModuleName({ isJiraApp })
  let composedQueryParameter = queryParameter ? `?${queryParameter}` : ''

  if (
    !composedQueryParameter &&
    source === EXTERNAL_LINK_HANDLER_SOURCES.WORKSPACE_NAME_CELL &&
    withEmptyFilters
  ) {
    composedQueryParameter = `?${getOkrElementsListQuery({ withEmptyFilters })}`
  }

  if (!!referer && !!composedQueryParameter) {
    composedQueryParameter = `${composedQueryParameter}&${REFERER}=${referer}`
  }

  if (isFirefox() && source === EXTERNAL_LINK_HANDLER_SOURCES.COMMENT && !!composedQueryParameter) {
    composedQueryParameter = `${composedQueryParameter}&${OPENED_NOTIFICATIONS_KEY}=true`
  }

  return composedQueryParameter
}

export const EXTERNAL_LINK_HANDLER_SOURCES = {
  COMMENT: 'comment',
  INTERVALS_PAGE: 'intervals-page',
  WORKSPACE_NAME_CELL: 'workspace-name-cell',
  JIRA_DASHBOARD_GADGET: 'jira-dashboard-gadget',
  JIRA_ISSUE_WIDGET: 'jira-issue-widget',
  CONFLUENCE_MACRO: 'confluence-macro',
  USER_DETAILS_PAGE: 'user-details-page',
  PLATFORM_ELEMENT_LINK: 'platform-element-link'
}

export const getHashFromLocation = () => {
  return location.hash.startsWith('#') ? '#' : ''
}

export const OPEN_LINK_TARGETS = {
  SELF: '_self',
  BLANK: '_blank',
  PARENT: '_parent',
  TOP: '_top',
  UNFENCED_TOP: '_unfencedTop'
}

const openLinkInNewTabWithForm = url => {
  const dummy = document.createElement('form')
  dummy.action = url
  dummy.method = 'GET'
  dummy.target = OPEN_LINK_TARGETS.BLANK
  dummy.style.display = 'none'
  document.body.appendChild(dummy)
  dummy.submit()
  document.body.removeChild(dummy)
}

const openLinkInNewTabWithAnchor = url => {
  const dummy = document.createElement('a')
  dummy.href = url
  dummy.target = OPEN_LINK_TARGETS.BLANK
  dummy.style.display = 'none'
  document.body.appendChild(dummy)
  dummy.click()
  document.body.removeChild(dummy)
}

export const replaceForgeLocationHash = async ({ hash = '', fresh = true } = {}) => {
  const location = await getForgeExtensionLocation({ fresh })

  if (!location) return null

  // `location` is a full URL that includes the Vue route in its hash.
  // We store the route in the URL hash so it appears in the browser address bar.
  const url = new URL(location)

  url.hash = hash
  return url.toString()
}

export const replaceAzureLocationHash = async ({ hash = '' } = {}) => {
  const location = await getAzureExtensionLocation()

  if (!location) return null

  // `location` is the host page URL (dev.azure.com/{org}/{project}/_apps/hub/...).
  // We store the Vue route in the URL hash so it appears in the browser address bar.
  const url = new URL(location)

  url.hash = hash

  return url.toString()
}

export const composeUrlSegments = (...segments) =>
  segments.map(removeUnnecessarySlashes).filter(Boolean).join('/')

/**
 * Builds a Forge app URL from the provided parts.
 *
 * @param {object} [options={}]
 * @param {string} [options.siteUrl]
 * @param {string} [options.environmentId=FORGE_ENVIRONMENT_ID]
 * @param {string} [options.hash='']
 * @returns {string}
 */
export const composeForgeAppUrl = ({
  siteUrl,
  environmentId = FORGE_ENVIRONMENT_ID,
  hash = ''
} = {}) => {
  const url = new URL(composeUrlSegments(siteUrl, 'jira', 'apps', FORGE_APP_ID, environmentId))

  url.hash = hash

  return url.toString()
}

/**
 * Builds a Forge app URL using the current Forge context.
 *
 * @param {string} [hash='']
 * @returns {Promise<string>}
 * @throws {Error} If environmentId or siteUrl are missing
 */
const buildForgeAppUrl = async (hash = '') => {
  const context = await getForgeContext({ fresh: true })

  const { environmentId, siteUrl } = context

  if (!environmentId || !siteUrl) {
    throw new Error('missing environmentId or siteUrl')
  }

  return composeForgeAppUrl({
    siteUrl,
    environmentId,
    hash
  })
}

/**
 * Builds a Forge app URL from a Confluence context.
 * Always uses the static FORGE_ENVIRONMENT_ID regardless of context because of Confluence
 * has its own context and `environmentId` mismatch with jira app
 *
 * @param {string} [hash='']
 * @returns {Promise<string>}
 * @throws {Error} If siteUrl is missing from context
 */
const buildForgeAppUrlForConfluence = async (hash = '') => {
  const context = await getForgeContext({ fresh: true })

  const { siteUrl } = context

  if (!siteUrl) {
    throw new Error('Missing siteUrl')
  }

  return composeForgeAppUrl({
    siteUrl,
    hash
  })
}

/**
 * Resolves the Azure host base URL and current project, or null when the SDK
 * or Location service is unavailable (warns in that case).
 * @returns {Promise<{ baseUrl: string, project: { name: string } } | null>}
 */
const getAzureUrlContext = async () => {
  if (!AZURE_DEVOPS_SDK) {
    console.warn('Azure DevOps SDK is not available')
    return null
  }

  const locationService = await getAzureService(CommonServiceIds.LocationService)

  if (!locationService) {
    console.warn('Azure Location Service is not available')
    return null
  }

  const project = getAzureProjectContext()
  const baseUrl = await locationService.getServiceLocation()

  return { baseUrl, project }
}

const buildAzureAppUrl = async (hash = '') => {
  const context = await getAzureUrlContext()

  if (!context) return

  const { baseUrl, project } = context

  if (!project) return

  const contributionId = AZURE_DEVOPS_SDK.getContributionId()

  const url = new URL(composeUrlSegments(baseUrl, project.name, '_apps', 'hub', contributionId))

  url.hash = hash

  return url.toString()
}

const openWorkItemUrl = async workItemId => {
  const workItemNavService = await getWorkItemNavService()

  if (!workItemNavService) {
    console.warn('Azure Work Item navigation service is not available')
    return
  }

  await workItemNavService.openWorkItem(Number(workItemId), true)
}

/**
 * Opens a URL in a new window or tab.
 *
 * @param {Object} [options={}] - Configuration options for opening the URL.
 * @param {string|null} [options.url=null] - The URL to open. If not provided, the function does nothing.
 * @param {boolean} [options.useBrowserWorkaround=false] - Whether to use a workaround for opening the URL in the browser.
 * @param {'_self' | '_blank' | '_parent' | '_top' | '_unfencedTop'} [options.target='_blank'] - The target where the URL will be opened. Defaults to '_blank'.
 * Allowed values: '_self', '_blank', '_parent', '_top', '_unfencedTop'.
 */
export const openLink = ({
  url = null,
  target = OPEN_LINK_TARGETS.BLANK,
  useBrowserWorkaround = false
} = {}) => {
  if (!url) {
    throw new Error('Argument [url] is required')
  }

  const [openLinkMethod] = [
    useBrowserWorkaround && isSafari() && openLinkInNewTabWithForm,
    useBrowserWorkaround && isFirefox() && openLinkInNewTabWithAnchor,
    () => window.open(url, target)
  ].filter(Boolean)

  openLinkMethod?.(url)
}

export const removeUnnecessarySlashes = str => {
  return String(str).replace(/^\/|\/$/g, '')
}

export const getDefaultAppRoute = ({ workspaceId, query }) => {
  if (!String(workspaceId)) {
    throw new Error('workspaceId is required')
  }

  const path = composeUrlSegments(
    ROUTE_PATHS.WORKSPACES,
    workspaceId,
    ROUTE_PATHS.OBJECTIVES,
    ROUTE_PATHS.ALIGNMENT,
    ROUTE_PATHS.OKR_ELEMENTS_HIERARCHY_TABLE
  )

  return `/${path}${query}`
}
/**
 * Navigates to an app route or opens it in a new tab,
 * depending on the platform, browser, and source context.
 *
 * @param {object} params
 * @param {string} params.id - Entity ID
 * @param {number|string} params.typeId
 * @param {number|string} params.intervalId
 * @param {number|string} params.levelId
 * @param {number|string|null} params.workspaceId
 * @param {string} params.source - Source context where the link was triggered (required for Jira)
 * @param {boolean} [params.withEmptyFilters=false]
 * @param {string} [params.appPlatform=APP_PLATFORMS.JIRA]
 * @param {import('vue-router').Router|null} [params.router=null]
 * @returns {void}
 */
export const externalLinkHelper = ({
  id,
  typeId,
  intervalId,
  levelId,
  workspaceId,
  source,
  withEmptyFilters = false,
  appPlatform = APP_PLATFORMS.JIRA,
  router = null
}) => {
  const { isJiraApp, isWebApp, isCrossPlatformApp, isAzureApp } = getAppType({ appPlatform })

  const composedQueryParameter = createQueryForExternalLink({
    id,
    typeId,
    intervalId,
    levelId,
    source,
    withEmptyFilters,
    isJiraApp
  })

  const DEFAULT_APP_ROUTE = getDefaultAppRoute({ workspaceId, query: composedQueryParameter })

  const APP_ROUTE =
    source === EXTERNAL_LINK_HANDLER_SOURCES.USER_DETAILS_PAGE
      ? `/workspaces/settings/workspaces/?${EDIT_WORKSPACE_QUERY_KEY}=${workspaceId}`
      : DEFAULT_APP_ROUTE

  if (!isNull(router)) {
    router.replace(APP_ROUTE)
    return
  }

  const showDevNotification = isAzureApp ? IS_DEVELOPMENT && !AZURE_DEVOPS_SDK : IS_DEVELOPMENT

  if (showDevNotification) {
    showNotify({
      type: NOTIFICATION_TYPES.WARNING,
      content:
        'You are in <b>DEVELOPMENT</b> mode, so your link will be opened on <b>localhost</b> domain. <br>If You want to test real functionality of <pre>externalLinkHelper</pre> please comment <b>this condition</b>.',
      expanded: true,
      isHtml: true
    })
    setTimeout(() => {
      openLink({
        url: `${location.origin}${getHashFromLocation()}${APP_ROUTE}`
      })
    }, 200)

    return
  }

  const { CONFLUENCE_MACRO, PLATFORM_ELEMENT_LINK } = EXTERNAL_LINK_HANDLER_SOURCES

  if (isJiraApp) {
    if (!source) {
      throw new Error('Argument [source] is required')
    }

    const _isFirefox = isFirefox()

    if (_isFirefox && !forgeRouter) {
      console.warn('Forge router is not available')
      return
    }

    const dispatchUrl = url => {
      if (_isFirefox) {
        forgeRouter.open(url)
        return
      }

      openLink({ url })
    }

    if (source === CONFLUENCE_MACRO) {
      buildForgeAppUrlForConfluence(APP_ROUTE)
        .then(dispatchUrl)
        .catch(error => {
          console.error('Error building Forge app URL for Confluence:', error)
        })

      return
    }

    if (source === PLATFORM_ELEMENT_LINK && _isFirefox) {
      forgeRouter.open({
        target: 'issue',
        issueKey: id
      })

      return
    }

    buildForgeAppUrl(APP_ROUTE)
      .then(dispatchUrl)
      .catch(error => {
        console.error('Error building Forge app URL:', error)
      })
  } else if (isAzureApp) {
    if (!source) {
      throw new Error('Argument [source] is required')
    }

    if (source === PLATFORM_ELEMENT_LINK) {
      openWorkItemUrl(id)
      return
    }

    buildAzureAppUrl(APP_ROUTE)
      .then(url => {
        dispatchUrlByAzureNavService(url)
      })
      .catch(error => {
        console.error('Error building Azure app URL:', error)
      })
  } else if (isWebApp) {
    openLink({
      url: `${location.origin}${getHashFromLocation()}${APP_ROUTE}`
    })
  } else if (isCrossPlatformApp) {
    openLink({
      url: `${location.origin}${getHashFromLocation()}${APP_ROUTE}`
    })
    // code below opens link in new tab without app environment where we built-in with iframe
    // openLink(
    //   {
    //     url: `${location.origin}${location.pathname}${location.search}${getHashFromLocation()}${APP_ROUTE}`
    //   }
    // )
  }
}
